import { useDispositions } from '@/hooks/useCalling'
import type { AutomationTriggerFilter } from '@/types/api/automation'
import { cn } from '@/utils/cn'

const OUTCOMES = [
  { value: 'ANSWERED', label: 'Answered' },
  { value: 'NO_ANSWER', label: 'No answer / missed' },
  { value: 'BUSY', label: 'Busy' },
  { value: 'NOT_CONNECTED', label: 'Not connected (phone)' },
  { value: 'AGENT_UNAVAILABLE', label: "Agent didn't answer" },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELED', label: 'Canceled' },
]

const PROVIDERS = [
  { value: 'EXOTEL', label: 'Click-to-call' },
  { value: 'MOBILE_SIM', label: "Agent's phone" },
]

interface Props {
  /** CALL_ENDED or CALL_DISPOSITION_SET */
  trigger: string
  value: AutomationTriggerFilter
  onChange: (value: AutomationTriggerFilter) => void
}

/** Which calls fire a call trigger. Nothing selected in a row means "any". */
export function CallTriggerFilterPicker({ trigger, value, onChange }: Props) {
  const { data: dispositions = [] } = useDispositions(false, trigger === 'CALL_DISPOSITION_SET')

  const toggle = (key: keyof AutomationTriggerFilter, item: string) => {
    const current = value[key] ?? []
    const next = current.includes(item) ? current.filter((v) => v !== item) : [...current, item]
    onChange({ ...value, [key]: next })
  }

  return (
    <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3">
      <p className="text-xs text-neutral-500">Only run for these calls (leave a row empty for any):</p>
      <ChipRow label="Outcome" options={OUTCOMES} selected={value.outcomes ?? []} onToggle={(v) => toggle('outcomes', v)} />
      {trigger === 'CALL_DISPOSITION_SET' && (
        <ChipRow
          label="Disposition"
          options={dispositions.map((d) => ({ value: d.id, label: d.label }))}
          selected={value.dispositionIds ?? []}
          onToggle={(v) => toggle('dispositionIds', v)}
        />
      )}
      <ChipRow label="Called with" options={PROVIDERS} selected={value.providerTypes ?? []} onToggle={(v) => toggle('providerTypes', v)} />
    </div>
  )
}

function ChipRow({ label, options, selected, onToggle }: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-24 shrink-0 text-xs font-medium text-neutral-600">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={selected.includes(option.value)}
          onClick={() => onToggle(option.value)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs transition-colors',
            selected.includes(option.value)
              ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800'
              : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
